import '../components/AddContent/LinkItem/styles.scss';

import {
  AccountAssignmentControl,
  AccountAssignmentLabel,
  AccountAssignmentTrigger,
  AccountPlaylistBanner,
  AccountPlaylistBannerDescription,
  AccountPlaylistBannerEmphasis,
  ButtonWrapper,
  ButtonsWrapper,
  ContentHeaderWrapper,
  ContentTitle,
  ContentTitleSection,
  ContentTitleWrapper,
  ContentWrapper,
  DefaultDeadlineButtonWrapper,
  DefaultDeadlineIconSlot,
  Description,
  DragHandleWrapper,
  FixedBottomButton,
  GroupItemMainWrapper,
  GroupItemRow,
  GroupItemsContainer,
  GroupItemsEmpty,
  Header,
  HeaderAccountLabel,
  HeaderTitle,
  HeaderTitleText,
  MainContent,
  PlaylistUsersSummary,
  SectionContentWrapper,
  SectionHeader,
  SectionTitle,
  SmallInfo,
  SonarHeaderActivity,
  SonarHeaderInfo,
  SubHeader,
  TabsWrapper,
} from './styles';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import {
  DeadlinePolicy,
  IContentGroup,
  IContentItem,
  IPlaylist,
  IPlaylistUserListItemResponse,
} from 'contexts/playlists/types';
import { PLAN_ENTERPRISE_PLUS_LAB, PLAN_LAB_EXPERIENCE } from 'constants/plans_config';
import { durationFromSecs, validUUID } from 'utils/helpers';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import AddContent from '../components/AddContent';
import AddEditCategory from '../components/AddEditSection';
import AssignPlaylistButton from './AssignPlaylistButton';
import AssignUsers from '../components/AssignUsers';
import Button from 'components/button/Button';
import ManageDeadlineDrawer from '../components/ManageDeadlineDrawer';
import DefaultDeadlineDrawer from '../components/DefaultDeadlineDrawer';
import { TeamDeadlineRow } from 'components/teamSelect/TeamAssignmentDeadlineMenu';
import { canManageTeamDeadline } from 'components/teamSelect/canManageTeamDeadline';
import ButtonIcon from 'components/buttonIcon';
import {
  ACCOUNT_DEADLINE_DISABLE_CONFIRMATION_DESCRIPTION,
  ACCOUNT_PLAYLIST_ERROR_MESSAGE,
  ACCOUNT_PLAYLIST_MESSAGES,
  ACCOUNT_PLAYLIST_TOGGLE_TOOLTIPS,
  ACCOUNT_PLAYLIST_TOOLTIPS,
  ADD_USERS_LABEL,
  ADD_USERS_ONLY_LABEL,
  ALL_USERS_ASSIGNED_TOOLTIP,
  CONTENT_TYPES,
  DEFAULT_DEADLINE_COPY,
  DEFAULT_DEADLINE_ERROR_MESSAGE,
  DELETE_PLAYLIST_CONFIRMATION_DESCRIPTION,
  DRAG_TO_REARRANGE_LABEL,
  PLAYLIST_METHOD,
  PLAYLIST_RULE_SCOPE_TEAM,
  REMOVE_TEAM_ASSIGNMENT_CONFIRMATION_DESCRIPTION,
  TEAM_DEFAULT_DEADLINE_ERROR_MESSAGE,
} from 'constants/playlists';
import { ConfirmModal } from 'components/modal';
import type { ConfirmModalProps } from 'components/modal';
import CourseItemData from './GroupItem/CourseItemData';
import CreatePlaylist from '../components/CreatePlaylist';
import {
  DurationPill,
  DurationPillSpinnerContainer,
} from 'pages/Playlists/components/PlaylistCard/styles';
import GroupItemImg from './GroupItem/GroupItemImg';
import { INE_URL } from 'constants/domains';
import { INE_UUID } from 'constants/settings';
import Icon from 'components/icons';
import LabItemData from './GroupItem/LabItemData';
import LearningPathDetail from './GroupItem/LearningPath/LearningPathDetail';
import LearningPathItemData from './GroupItem/LearningPath/LearningPathItemData';
import CollectionItemData, { isCollectionUnavailable } from './GroupItem/CollectionItemData';
import CollectionDetail from './GroupItem/Collection/CollectionDetail';
import LinkItemData from './GroupItem/LinkItemData';
import Notification from 'components/notification';
import { PLAYLIST_TYPE } from 'constants/playlists';
import PlaylistChangelogModal from './PlaylistChangelog/PlaylistChangelogModal';
import PlaylistDeadlineWarningBanner from '../components/PlaylistDeadlineWarningBanner';
import PlaylistsProvider from 'contexts/playlists';
import { SessionContext } from 'contexts/session';
import { LoadingOutlined } from '@ant-design/icons';
import { Skeleton } from 'antd';
import SonarPlaylistLogo from 'assets/images/sonar-playlist.png';
import { Spinner } from 'components/loadings';
import Tabs from 'components/tabs';
import Tooltip from 'components/tooltip';
import UsersAssigned from '../components/UsersAssigned';
import VideoItemData from './GroupItem/VideoItemData';
import cloneDeep from 'lodash.clonedeep';
import { colors } from 'constants/styles/colors';
import formatDistance from 'date-fns/formatDistance';
import { isMobile } from 'react-device-detect';
import { patchLink } from 'services/links';
import { getDeadlinePolicy, updatePlaylistRule } from 'services/playlists';
import usePlaylistData from 'hooks/usePlaylistData';
import useWindowSize from 'hooks/useWindowSize';
import AccountPlaylistToggle, { AccountPlaylistBadge } from '../components/AccountPlaylistToggle';
import {
  createAccountPlaylistToggleStore,
  getAccountPlaylistCooldownSecondsRemaining,
} from '../components/AccountPlaylistToggle/AccountPlaylistToggle.state';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PlaylistUnassignTeamSelector from 'components/TeamSelector/PlaylistUnassignTeamSelector';
import { UserTeam } from 'contexts/teams/types';
import RadioPopoverDropdown, {
  type RadioPopoverOption,
} from 'components/dropdown/RadioPopoverDropdown';
import { pluralize } from 'utils/string';
import {
  PLAYLIST_DURATION_REFRESH_INTERVAL_MS,
  PLAYLIST_DURATION_REFRESH_MAX_RETRIES,
  clearPlaylistDurationRefreshMarker,
  isPlaylistDurationRefreshMarkerExpired,
  readPlaylistDurationRefreshMarker,
  writePlaylistDurationRefreshMarker,
} from './playlistDurationRefreshMarker';

const editPlaylistState = {
  open: false,
  state: { title: null, confirmLabel: null, method: null, playlist: null, playlistId: null },
};

type ConfirmModalState = Omit<ConfirmModalProps, 'title' | 'onCancel'>;

const confirmModalDefaultState: ConfirmModalState = {
  open: false,
  onAccept: () => undefined,
  description: '',
  acceptLabel: 'Yes',
  cancelLabel: 'No',
};

const isPlaylistUserCompleted = (playlistUser: IPlaylistUserListItemResponse): boolean =>
  Boolean(playlistUser.finished_date) ||
  playlistUser.progress >= 1 ||
  (playlistUser.trackable_items_total > 0 &&
    playlistUser.trackable_items_finished >= playlistUser.trackable_items_total);

// Only the key set follows the playlist; whatever the user expanded is preserved,
// otherwise a refresh or drag-reorder closes an open panel mid-interaction.
export const reconcileCollapsedState = (
  prev: Record<string, boolean> | null,
  nextUuids: string[]
): Record<string, boolean> | null => {
  const prevCollapsed = prev ?? {};

  if (nextUuids.length === 0) {
    return Object.keys(prevCollapsed).length === 0 ? prev : null;
  }

  const merged = nextUuids.reduce<Record<string, boolean>>((acc, uuid) => {
    acc[uuid] = prevCollapsed[uuid] ?? false;
    return acc;
  }, {});

  const unchanged =
    Object.keys(prevCollapsed).length === nextUuids.length &&
    nextUuids.every((uuid) => prevCollapsed[uuid] === merged[uuid]);

  return unchanged ? prev : merged;
};

const PlaylistDetail = (): JSX.Element => {
  const {
    dashboardAccountPlaylistAssignment,
    dashboardTeamsPlaylistAssignment,
    playlistDeadlinesMvp,
    playlistDeadlinesPhase2,
  } = useFlags() || {};
  const isAccountPlaylistFeatureEnabled = Boolean(dashboardAccountPlaylistAssignment);
  const isTeamsPlaylistAssignmentEnabled = Boolean(dashboardTeamsPlaylistAssignment);
  const {
    playlist,
    fetching,
    setFetching,
    fetchingUsers,
    playlistUsers,
    playlistUsersSummary,
    usersPagination,
    selectedDeadlineFilters,
    sortState,
    refreshPlaylist,
    isLastRefreshRetryable,
    callEditPlaylist,
    callDeletePlaylist,
    callGetUsers,
    onDeadlineFiltersChange,
    onSortToggle,
    onSortSelect,
    callRemoveUser,
    setPlaylist,
    onUsersPaginationChange,
    callGetAssignedTeams,
  } = usePlaylistData();

  const isSonar = useMemo(() => {
    return [PLAYLIST_TYPE.SONAR_RECOMMENDED, PLAYLIST_TYPE.SONAR_GOAL].includes(playlist.type);
  }, [playlist]);

  const isSonarRecommended = useMemo(() => {
    return PLAYLIST_TYPE.SONAR_RECOMMENDED === playlist.type;
  }, [playlist]);

  const isSonarGoal = useMemo(() => {
    return PLAYLIST_TYPE.SONAR_GOAL === playlist.type;
  }, [playlist]);

  const [showChangelog, setShowChangelog] = useState(false);

  const tabItems = [
    { name: 'Playlist Content', value: 'content' },
    { name: !isSonar ? 'Users assigned' : 'Playlist Progress', value: 'users' },
  ];

  const [confirmModalState, setConfirmModalState] = useState(confirmModalDefaultState);
  const [addContentVisible, setAddContentVisible] = useState(false);
  const [addingUsers, setAddingUsers] = useState(false);
  const [dropType, setDropType] = useState('');
  const { isScreenSm, isScreenLg } = useWindowSize();
  const [categoryToAddContent, setCategoryToAddContent] = useState('');
  const [selectedTab, setSelectedTab] = useState(tabItems[0].value);

  const [editingPlaylist, setEditingPlaylist] = useState(editPlaylistState);

  const [addingSection, setAddingSection] = useState(false);

  const [groupToEdit, setGroupToEdit] = useState<IContentGroup | null>(null);

  const [collapsed, setCollapsed] = useState(false);
  const [showDeadlineUpdateWarning, setShowDeadlineUpdateWarning] = useState(false);
  const [isPlaylistDurationRefreshing, setIsPlaylistDurationRefreshing] = useState(false);

  const [learningPathCollapsed, setLearningPathCollapsed] = useState<Record<
    string,
    boolean
  > | null>(null);
  const [hasLoadedUsersOnce, setHasLoadedUsersOnce] = useState(false);
  const [hasStartedUsersFetch, setHasStartedUsersFetch] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [bulkDeadlineDrawerVisible, setBulkDeadlineDrawerVisible] = useState(false);
  const [selectedDeadlineUser, setSelectedDeadlineUser] = useState<{
    userId: string;
    currentDeadline: {
      due_date: string | null;
      deadline_type: 'custom' | 'recommended' | null;
      training_pace?: number | null;
    } | null;
  } | null>(null);

  // Persist account playlist state so header and actions reflect backend truth.
  const [accountPlaylistState, setAccountPlaylistState] = useState({
    isAccountPlaylist: Boolean(playlist?.is_account_playlist),
    isCoolingDown: false,
    cooldownEndsAt: null as number | null,
    cooldownSecondsRemaining: 0,
    isLoading: false,
  });
  const accountPlaylistToggleStore = useMemo(() => createAccountPlaylistToggleStore(), []);
  const [defaultDeadlineDrawerVisible, setDefaultDeadlineDrawerVisible] = useState(false);
  const [defaultDeadlinePolicy, setDefaultDeadlinePolicy] = useState<DeadlinePolicy | null>(null);
  // True only once the initial account-policy fetch has settled; false while a fetch is in flight
  // (including right after the account assignment is (re-)enabled), so a fast toggle-off before
  // that fetch settles still triggers the disable confirmation.
  const [defaultDeadlinePolicyKnown, setDefaultDeadlinePolicyKnown] = useState(false);
  const [defaultDeadlineLoading, setDefaultDeadlineLoading] = useState(false);
  const previousPlaylistIdRef = useRef<string | null>(null);
  const refreshPlaylistRef = useRef(refreshPlaylist);
  const isLastRefreshRetryableRef = useRef(isLastRefreshRetryable);
  const durationRefreshTimeoutRef = useRef<number | null>(null);
  const durationRefreshAttemptRef = useRef(0);
  const durationRefreshRunIdRef = useRef(0);
  const openTeamDeadlineRequestIdRef = useRef(0);
  const isMountedRef = useRef(true);

  const sonarBackground = isSonarGoal ? '--goal-green-glass' : '--business-purple-glass';

  // Update local state when playlist changes; cooldown comes from the UI mock, not the API.
  useEffect(() => {
    const isNewPlaylist = previousPlaylistIdRef.current !== playlist?.id;
    previousPlaylistIdRef.current = playlist?.id || null;

    if (isNewPlaylist) {
      setDefaultDeadlinePolicy(null);
      setDefaultDeadlineDrawerVisible(false);
      setDefaultDeadlinePolicyKnown(false);
      setTeamDeadlineDrawerTeamId(null);
      setTeamDeadlineDrawerVisible(false);
      setTeamDeadlinePolicy(null);
      setOpeningTeamDeadlineDrawerTeamId(null);
      // Invalidate any in-flight getDeadlinePolicy request for the old playlist so a late
      // resolution can't reopen the drawer with data bound to the wrong playlist.
      openTeamDeadlineRequestIdRef.current += 1;
    }

    setAccountPlaylistState((prev) => {
      const nextIsAccountPlaylist = Boolean(playlist?.is_account_playlist);
      if (!isNewPlaylist && prev.isAccountPlaylist === nextIsAccountPlaylist) {
        return prev;
      }

      return {
        isAccountPlaylist: nextIsAccountPlaylist,
        isCoolingDown: isNewPlaylist ? false : prev.isCoolingDown,
        cooldownEndsAt: isNewPlaylist ? null : prev.cooldownEndsAt,
        cooldownSecondsRemaining: isNewPlaylist ? 0 : prev.cooldownSecondsRemaining,
        isLoading: isNewPlaylist ? false : prev.isLoading,
      };
    });
  }, [playlist?.id, playlist?.is_account_playlist]);

  const username = useMemo(() => {
    const createdFor = playlist?.created_for;
    if (!createdFor) return '';
    const { first_name, last_name } = createdFor;
    return `${first_name} ${last_name}`;
  }, [playlist]);

  const EXPANDABLE_CONTENT_TYPES = ['learning-path', 'collection'];

  // Locked, non-linkable and out-of-experience items keep a plain title: with no
  // handler the row renders neither the affordance nor the click target.
  // An unpublished or deleted collection has no page left to open.
  const isUnopenable = (groupItem: IContentItem) =>
    isContentLocked(groupItem) ||
    (groupItem.content_type === 'collection' && isCollectionUnavailable(groupItem));

  const openHandlerFor = (groupItem: IContentItem) =>
    !isUnopenable(groupItem) &&
    showItemLink(groupItem) &&
    checkLabExperience(groupItem.content_type, groupItem.is_lab_experience)
      ? () => handleOpenGroupItem(groupItem)
      : undefined;

  const showItemLink = (groupItem: IContentItem) => {
    return (
      isSonar ||
      ['course', 'link', 'lab', 'learning-path', 'collection', 'video'].includes(
        groupItem.content_type
      )
    );
  };

  const modifiedAgo = playlist.modified
    ? formatDistance(new Date(playlist.modified), new Date(), {
        addSuffix: true,
      })
    : null;

  const session = useContext(SessionContext);
  const { account } = session || {};
  const isAccountOwner = session?.isAccountOwner;
  const isTeamManager = session?.isTeamManager;
  const teams = session?.teams || [];
  // The account teams list (GET /teams/) has no is_manager; the user's memberships carry it.
  const userTeams = session?.user?.teams;
  const business_account_id = account?.id;

  const addons = account?.account_available_addons ?? [];
  // The session resolves the add-on by id; the name match is kept so accounts
  // whose payload only carries the display name are not newly locked.
  const hasSkillDiveAddon =
    Boolean(session?.hasSkillDive) || addons.some((ad) => ad.name === CONTENT_TYPES.SKILL_DIVE);
  const has_skill_dive =
    account?.pricing_plan === PLAN_LAB_EXPERIENCE ||
    account?.pricing_plan === PLAN_ENTERPRISE_PLUS_LAB ||
    hasSkillDiveAddon;

  const isAccountOwnerOrTeamManager = isAccountOwner || isTeamManager;

  const checkLabExperience = (content_type: string, is_lab_experience?: boolean) => {
    if (content_type === 'lab' && is_lab_experience) {
      return has_skill_dive;
    }
    return true;
  };

  // Collections are Skill Dive content, so they lock like Skill Dive labs. The
  // payload's `access` is an inert backend placeholder that never restricts, so
  // the plan is the only real signal until it is wired up.
  const isContentLocked = (groupItem: IContentItem) => {
    if (groupItem.content_type === 'collection') return !has_skill_dive;
    return Boolean(groupItem.is_lab_experience && !has_skill_dive);
  };

  // Keep toggle state aligned with backend values and cooldowns.
  const handleAccountPlaylistChange = useCallback(
    (state: {
      isAccountPlaylist: boolean;
      isCoolingDown: boolean;
      cooldownEndsAt: number | null;
      cooldownSecondsRemaining: number;
      isLoading: boolean;
    }) =>
      setAccountPlaylistState((prev) => {
        if (
          prev.isAccountPlaylist === state.isAccountPlaylist &&
          prev.isCoolingDown === state.isCoolingDown &&
          prev.cooldownEndsAt === state.cooldownEndsAt &&
          prev.cooldownSecondsRemaining === state.cooldownSecondsRemaining &&
          prev.isLoading === state.isLoading
        ) {
          return prev;
        }
        return state;
      }),
    []
  );

  // Refresh detail data + users after toggling.
  const handleAccountToggleRefresh = useCallback(async () => {
    await refreshPlaylist();
    await callGetUsers(isSonar);
  }, [callGetUsers, isSonar, refreshPlaylist]);

  useEffect(() => {
    const cooldownEndsAt = accountPlaylistState.cooldownEndsAt;
    if (!cooldownEndsAt) {
      setAccountPlaylistState((prev) => {
        if (!prev.isCoolingDown && prev.cooldownSecondsRemaining === 0) {
          return prev;
        }
        return { ...prev, isCoolingDown: false, cooldownSecondsRemaining: 0 };
      });
      return;
    }

    const updateRemaining = () => {
      const remainingSeconds = Math.ceil((cooldownEndsAt - Date.now()) / 1000);
      if (remainingSeconds <= 0) {
        setAccountPlaylistState((prev) => ({
          ...prev,
          isCoolingDown: false,
          cooldownSecondsRemaining: 0,
        }));
        return;
      }
      setAccountPlaylistState((prev) => {
        if (prev.isCoolingDown === true && prev.cooldownSecondsRemaining === remainingSeconds) {
          return prev;
        }
        return {
          ...prev,
          isCoolingDown: true,
          cooldownSecondsRemaining: remainingSeconds,
        };
      });
    };

    updateRemaining();
    const interval = window.setInterval(updateRemaining, 1000);
    return () => window.clearInterval(interval);
  }, [accountPlaylistState.cooldownEndsAt]);

  const applyAccountAssignChange = useCallback(
    async (enabled: boolean) => {
      if (!playlist?.id) return;

      setAccountPlaylistState((prev) => ({ ...prev, isLoading: true }));
      try {
        const response = await accountPlaylistToggleStore.setState({
          playlistId: playlist.id,
          enabled,
        });
        const cooldownSecondsRemaining = getAccountPlaylistCooldownSecondsRemaining(playlist.id);
        setAccountPlaylistState({
          isAccountPlaylist: response.is_account_playlist,
          isCoolingDown: cooldownSecondsRemaining > 0,
          cooldownEndsAt: response.cooldown_ends_at ?? null,
          cooldownSecondsRemaining,
          isLoading: false,
        });

        if (!enabled) {
          // The rule is deleted along with the account assignment; re-enabling starts empty.
          setDefaultDeadlinePolicy(null);
          setDefaultDeadlinePolicyKnown(true);
        }

        Notification({
          type: 'success',
          text: response.is_account_playlist
            ? ACCOUNT_PLAYLIST_MESSAGES.assigned
            : ACCOUNT_PLAYLIST_MESSAGES.unassigned,
          singleLine: true,
        });
        await handleAccountToggleRefresh();
      } catch (error) {
        setAccountPlaylistState((prev) => ({ ...prev, isLoading: false }));
        Notification({ type: 'error', text: ACCOUNT_PLAYLIST_ERROR_MESSAGE });
      }
    },
    [accountPlaylistToggleStore, handleAccountToggleRefresh, playlist?.id]
  );

  const handleAccountAssignChange = useCallback(
    async (nextValue: string) => {
      if (!playlist?.id) return;
      if (accountPlaylistState.isLoading || accountPlaylistState.isCoolingDown) return;
      const enabled = nextValue === 'on';

      // A policy fetch still in flight (not yet settled) is treated as "might exist" so a real
      // default deadline is never deleted without confirmation.
      if (!enabled && (defaultDeadlinePolicy || !defaultDeadlinePolicyKnown)) {
        setConfirmModalState({
          ...confirmModalDefaultState,
          open: true,
          description: ACCOUNT_DEADLINE_DISABLE_CONFIRMATION_DESCRIPTION,
          onAccept: async () => {
            setConfirmModalState(confirmModalDefaultState);
            await applyAccountAssignChange(false);
          },
        });
        return;
      }

      await applyAccountAssignChange(enabled);
    },
    [
      accountPlaylistState.isCoolingDown,
      accountPlaylistState.isLoading,
      applyAccountAssignChange,
      defaultDeadlinePolicy,
      defaultDeadlinePolicyKnown,
      playlist?.id,
    ]
  );

  // Prefetch the default deadline policy whenever the playlist is account-assigned, so the
  // toggle-off confirmation reflects reality even if the drawer was never opened this session.
  useEffect(() => {
    if (!playlistDeadlinesPhase2 || !isAccountOwner) return undefined;
    if (!playlist?.id || !accountPlaylistState.isAccountPlaylist) return undefined;

    let cancelled = false;
    setDefaultDeadlinePolicyKnown(false);
    getDeadlinePolicy(playlist.id, { scope: 'account' })
      .then(({ accountPolicy }) => {
        if (cancelled || !isMountedRef.current) return;
        setDefaultDeadlinePolicy(accountPolicy);
        setDefaultDeadlinePolicyKnown(true);
      })
      .catch(() => {
        // Leave as unknown; do not block the page on this best-effort prefetch.
      });

    return () => {
      cancelled = true;
    };
  }, [
    playlist?.id,
    accountPlaylistState.isAccountPlaylist,
    playlistDeadlinesPhase2,
    isAccountOwner,
  ]);

  const handleOpenDefaultDeadlineDrawer = useCallback(async () => {
    if (!playlist?.id) return;

    setDefaultDeadlineLoading(true);
    try {
      const { accountPolicy } = await getDeadlinePolicy(playlist.id, { scope: 'account' });
      if (isMountedRef.current) {
        setDefaultDeadlinePolicy(accountPolicy);
        setDefaultDeadlinePolicyKnown(true);
        setDefaultDeadlineDrawerVisible(true);
      }
    } catch (error) {
      Notification({ type: 'error', text: DEFAULT_DEADLINE_ERROR_MESSAGE });
    } finally {
      if (isMountedRef.current) {
        setDefaultDeadlineLoading(false);
      }
    }
  }, [playlist?.id]);

  const handleCloseDefaultDeadlineDrawer = useCallback(() => {
    setDefaultDeadlineDrawerVisible(false);
  }, []);

  const handleDefaultDeadlineSuccess = useCallback(async () => {
    if (playlist?.id) {
      try {
        const { accountPolicy } = await getDeadlinePolicy(playlist.id, { scope: 'account' });
        if (isMountedRef.current) {
          setDefaultDeadlinePolicy(accountPolicy);
          setDefaultDeadlinePolicyKnown(true);
        }
      } catch (error) {
        Notification({ type: 'error', text: DEFAULT_DEADLINE_ERROR_MESSAGE });
      }
    }
    await handleAccountToggleRefresh();
  }, [playlist?.id, handleAccountToggleRefresh]);

  const isAccountPlaylist = accountPlaylistState.isAccountPlaylist;
  const isAssignedToAccount =
    isAccountPlaylistFeatureEnabled && !fetchingUsers
      ? playlistUsersSummary?.is_account_playlist ?? accountPlaylistState.isAccountPlaylist
      : false;
  // Lock manual assignments during account-assigned/cooldown/loading periods.
  const disableManualAssignments =
    isAccountPlaylistFeatureEnabled &&
    (isAssignedToAccount || accountPlaylistState.isCoolingDown || accountPlaylistState.isLoading);
  const totalAssignedUsers = playlistUsersSummary?.total_users ?? playlistUsers.length;
  const totalAssignedUsersOverall =
    typeof playlist?.assigned_users_count === 'number'
      ? playlist.assigned_users_count
      : Array.isArray(playlist?.assigned_user_ids)
      ? playlist.assigned_user_ids.length
      : totalAssignedUsers;
  const allAssignedUserIds = useMemo(() => {
    const playlistAssignedIds =
      Array.isArray(playlist?.assigned_user_ids) && playlist.assigned_user_ids.length > 0
        ? playlist.assigned_user_ids
        : playlistUsers.map((playlistUser) => playlistUser.user.id);

    return Array.from(new Set(playlistAssignedIds.filter(Boolean)));
  }, [playlist?.assigned_user_ids, playlistUsers]);
  const deadlineBulkActionsEnabled = Boolean(playlistDeadlinesMvp && !isSonar && !isMobile);
  const allUsersSelected =
    deadlineBulkActionsEnabled &&
    allAssignedUserIds.length > 0 &&
    selectedUserIds.length === allAssignedUserIds.length;
  const hasPartiallySelectedUsers =
    deadlineBulkActionsEnabled && selectedUserIds.length > 0 && !allUsersSelected;
  const totalAccountUsersRaw = account?.users_count;
  const totalAccountUsers =
    typeof totalAccountUsersRaw === 'number'
      ? totalAccountUsersRaw
      : Number.isFinite(Number(totalAccountUsersRaw))
      ? Number(totalAccountUsersRaw)
      : null;
  const canShowAccountPlaylistToggle =
    isAccountPlaylistFeatureEnabled && !isSonar && isAssignedToAccount;

  const showAccountPlaylistToggleTM = canShowAccountPlaylistToggle && Boolean(isTeamManager);

  const showAccountPlaylistToggleAO =
    isAccountPlaylistFeatureEnabled && !isSonar && Boolean(isAccountOwner) && isAssignedToAccount;

  const canShowDefaultDeadlineEntryPoint = Boolean(
    playlistDeadlinesPhase2 && showAccountPlaylistToggleAO
  );

  const allUsersAssigned =
    !isSonar &&
    !isAccountPlaylist &&
    typeof totalAccountUsers === 'number' &&
    totalAccountUsers > 0 &&
    totalAssignedUsers >= totalAccountUsers;
  const addUsersLabel = isTeamsPlaylistAssignmentEnabled ? ADD_USERS_LABEL : ADD_USERS_ONLY_LABEL;
  // When dashboardTeamsPlaylistAssignment is ON, manually-assigned playlists keep the button
  // enabled so teams can still be assigned to them.
  const disableForManualAssignment = !isTeamsPlaylistAssignmentEnabled && allUsersAssigned;
  const disableAddUsers = disableManualAssignments || disableForManualAssignment;

  const cooldownTooltip = ACCOUNT_PLAYLIST_TOGGLE_TOOLTIPS.cooldown(
    Math.max(1, accountPlaylistState.cooldownSecondsRemaining)
  );

  const getAddUsersTooltip = () => {
    if (disableForManualAssignment) {
      return ALL_USERS_ASSIGNED_TOOLTIP;
    }

    if (accountPlaylistState.isCoolingDown) {
      return cooldownTooltip;
    }

    if (disableManualAssignments) {
      return ACCOUNT_PLAYLIST_TOOLTIPS.accountPlaylist;
    }

    return addUsersLabel;
  };

  const getTeamsDropdownTooltip = () => {
    if (accountPlaylistState.isLoading) {
      return ACCOUNT_PLAYLIST_TOGGLE_TOOLTIPS.loading;
    }

    if (accountPlaylistState.isCoolingDown) {
      return cooldownTooltip;
    }

    if (disableManualAssignments) {
      return ACCOUNT_PLAYLIST_TOOLTIPS.accountPlaylist;
    }

    return undefined;
  };

  const addUsersTooltip = getAddUsersTooltip();
  const teamsDropdownTooltip = getTeamsDropdownTooltip();

  const assignedUsersSummary = `Assigned to ${totalAssignedUsers} user${
    totalAssignedUsers === 1 ? '' : 's'
  }${isAssignedToAccount ? ' (entire account)' : ''}`;
  const accountAssignValue = isAssignedToAccount ? 'on' : 'off';
  const accountAssignTriggerLabel = `Entire account: ${accountAssignValue === 'on' ? 'On' : 'Off'}`;
  const accountAssignOptions = useMemo<RadioPopoverOption[]>(
    () => [
      {
        value: 'on',
        label: 'On: Assign to entire account',
        description:
          'The playlist will be assigned automatically to all current users and new users who join the account.',
      },
      {
        value: 'off',
        label: 'Off: Do not assign to entire account',
        description:
          'The playlist will not be assigned automatically. Assignments must be made at the user or team level.',
      },
    ],
    []
  );

  const assignedTeams = useMemo<UserTeam[]>(() => {
    const assigned = playlistUsersSummary?.teams_assigned || [];

    return assigned.reduce<UserTeam[]>((acc, assignedTeam) => {
      const fullTeam = teams?.find((t) => t.id === assignedTeam.id);
      if (!fullTeam) return acc;

      acc.push({
        team_id: assignedTeam.id,
        name: assignedTeam.name,
        is_manager: false,
        team: {
          ...fullTeam,
          active_users_count: assignedTeam.active_users_count,
        },
      } as UserTeam);

      return acc;
    }, []);
  }, [playlistUsersSummary, teams]);

  const isTeamsAssignmentEnabled = Boolean(dashboardTeamsPlaylistAssignment);
  const showTeamsAssigned =
    isTeamsAssignmentEnabled &&
    isAccountOwnerOrTeamManager &&
    assignedTeams?.length > 0 &&
    !isAssignedToAccount;
  const disablePlaylistUsersActions = fetching || fetchingUsers;
  const shouldShowPlaylistUsersActions = hasLoadedUsersOnce;

  useEffect(() => {
    if (!deadlineBulkActionsEnabled) {
      setSelectedUserIds((prev) => (prev.length > 0 ? [] : prev));
      return;
    }

    const validIds = new Set(allAssignedUserIds);
    setSelectedUserIds((prev) => {
      const nextSelectedIds = prev.filter((userId) => validIds.has(userId));
      return nextSelectedIds.length === prev.length ? prev : nextSelectedIds;
    });
  }, [allAssignedUserIds, deadlineBulkActionsEnabled]);

  const handleToggleUserSelection = useCallback((userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((selectedUserId) => selectedUserId !== userId)
        : [...prev, userId]
    );
  }, []);

  const handleToggleSelectAllUsers = useCallback(() => {
    if (allUsersSelected) {
      setSelectedUserIds([]);
      return;
    }

    setSelectedUserIds(allAssignedUserIds);
  }, [allAssignedUserIds, allUsersSelected]);

  const handleClearSelection = useCallback(() => {
    setSelectedUserIds([]);
  }, []);

  const selectedDeadlineUserProgress = useMemo(
    () =>
      selectedDeadlineUser
        ? playlistUsers.find((playlistUser) => playlistUser.user.id === selectedDeadlineUser.userId)
            ?.progress ?? 0
        : 0,
    [playlistUsers, selectedDeadlineUser]
  );

  const singleSelectedBulkUserProgress = useMemo(() => {
    const selectedPlaylistUser = playlistUsers.find(
      (playlistUser) => playlistUser.user.id === selectedUserIds[0]
    );

    if (!selectedPlaylistUser || isPlaylistUserCompleted(selectedPlaylistUser)) {
      return 0;
    }

    return selectedPlaylistUser.progress;
  }, [playlistUsers, selectedUserIds]);

  const hasCompletedUsersInBulkSelection = useMemo(() => {
    if (!selectedUserIds.length) {
      return false;
    }

    const selectedIdsSet = new Set(selectedUserIds);

    return playlistUsers.some((playlistUser) => {
      if (!selectedIdsSet.has(playlistUser.user.id)) {
        return false;
      }

      return isPlaylistUserCompleted(playlistUser);
    });
  }, [playlistUsers, selectedUserIds]);

  const handleOpenUserDeadline = useCallback(
    (userId: string, deadline: IPlaylistUserListItemResponse['deadline']) => {
      setSelectedDeadlineUser({
        userId,
        currentDeadline: deadline
          ? {
              due_date: deadline.due_date ?? null,
              deadline_type: deadline.deadline_type,
              ...(deadline.training_pace != null ? { training_pace: deadline.training_pace } : {}),
            }
          : null,
      });
    },
    []
  );

  const handleCloseUserDeadline = useCallback(() => {
    setSelectedDeadlineUser(null);
  }, []);

  const handleDeadlineSuccess = useCallback(async () => {
    setSelectedDeadlineUser(null);
    await refreshPlaylist();
    await callGetUsers(isSonar);
  }, [callGetUsers, isSonar, refreshPlaylist]);

  const handleBulkDeadlineSuccess = useCallback(async () => {
    setSelectedUserIds([]);
    setBulkDeadlineDrawerVisible(false);
    await refreshPlaylist();
    await callGetUsers(isSonar);
  }, [callGetUsers, isSonar, refreshPlaylist]);

  const generateContentFilteringGroupItem = (course, group) => {
    const normalizedContent = cloneDeep(playlist.content);

    // search for the category and adapt payload
    for (let index = 0; index < normalizedContent.length; index++) {
      const groupElement = normalizedContent[index];
      if (groupElement.name === group.name) {
        groupElement.content = groupElement.content.filter((c) => c.uuid !== course.uuid);
      }
      for (let j = 0; j < groupElement.content.length; j++) {
        const { uuid, content_type } = groupElement.content[j];
        groupElement.content[j] = { uuid, content_type };
      }
    }
    return normalizedContent;
  };

  const generateContentFilteringSection = (group) => {
    let normalizedContent = cloneDeep(playlist.content);

    normalizedContent = normalizedContent.filter((s) => s.name !== group.name);

    for (let index = 0; index < normalizedContent.length; index++) {
      const groupElement = normalizedContent[index];
      for (let j = 0; j < groupElement.content.length; j++) {
        const { uuid, content_type } = groupElement.content[j];

        groupElement.content[j] = { uuid, content_type };
      }
    }

    return normalizedContent;
  };

  const handlePatchLink = async (uuid: string, field: string, value: string) => {
    setFetching(true);
    try {
      const response = await patchLink({ link: { id: uuid, [field]: value }, business_account_id });
      if (response.status === 200) {
        Notification({ text: `${response.data.name} link has been updated`, type: 'success' });
        refreshPlaylist();
        setFetching(false);
      } else {
        Notification({ text: 'There has been a problem editing your link', type: 'error' });
      }
    } catch (error) {
      if (error?.response?.data?.details) {
        Notification({ text: error.response.data.details, type: 'error' });
      }
    }
    setFetching(false);
  };

  const maybeShowDeadlineUpdateWarning = (shouldShow?: boolean) => {
    if (shouldShow) {
      setShowDeadlineUpdateWarning(true);
    }
  };

  const clearDurationRefreshTimeout = useCallback(() => {
    if (durationRefreshTimeoutRef.current != null) {
      window.clearTimeout(durationRefreshTimeoutRef.current);
      durationRefreshTimeoutRef.current = null;
    }
  }, []);

  const stopDurationRefresh = useCallback(() => {
    clearDurationRefreshTimeout();
    if (isMountedRef.current) {
      setIsPlaylistDurationRefreshing(false);
    }
  }, [clearDurationRefreshTimeout]);

  useEffect(() => {
    refreshPlaylistRef.current = refreshPlaylist;
    isLastRefreshRetryableRef.current = isLastRefreshRetryable;
  }, [refreshPlaylist, isLastRefreshRetryable]);

  const runPlaylistDurationRefresh = useCallback(async () => {
    if (!playlist?.id) return;

    const marker = readPlaylistDurationRefreshMarker(playlist.id);
    if (!marker) {
      stopDurationRefresh();
      return;
    }

    if (marker.playlistId !== playlist.id || isPlaylistDurationRefreshMarkerExpired(marker)) {
      clearPlaylistDurationRefreshMarker(playlist.id);
      stopDurationRefresh();
      return;
    }

    const runId = ++durationRefreshRunIdRef.current;
    durationRefreshAttemptRef.current = 0;
    setIsPlaylistDurationRefreshing(true);

    const executeAttempt = async () => {
      if (!isMountedRef.current || runId !== durationRefreshRunIdRef.current) return;

      const currentMarker = readPlaylistDurationRefreshMarker(playlist.id);
      if (!currentMarker) {
        stopDurationRefresh();
        return;
      }

      if (
        currentMarker.playlistId !== playlist.id ||
        isPlaylistDurationRefreshMarkerExpired(currentMarker)
      ) {
        clearPlaylistDurationRefreshMarker(playlist.id);
        stopDurationRefresh();
        return;
      }

      if (durationRefreshAttemptRef.current >= PLAYLIST_DURATION_REFRESH_MAX_RETRIES) {
        clearPlaylistDurationRefreshMarker(playlist.id);
        stopDurationRefresh();
        return;
      }

      durationRefreshAttemptRef.current += 1;
      const refreshedPlaylist = await refreshPlaylistRef.current({ silent: true });

      if (!isMountedRef.current || runId !== durationRefreshRunIdRef.current) return;

      if (
        refreshedPlaylist &&
        refreshedPlaylist.duration_in_seconds !== currentMarker.baselineDurationSeconds
      ) {
        clearPlaylistDurationRefreshMarker(playlist.id);
        stopDurationRefresh();
        return;
      }

      // A falsy result from a non-retryable failure (e.g. a 4xx) means asking again won't
      // help, unlike "duration hasn't changed yet" — stop instead of burning every attempt.
      if (!refreshedPlaylist && !isLastRefreshRetryableRef.current()) {
        clearPlaylistDurationRefreshMarker(playlist.id);
        stopDurationRefresh();
        return;
      }

      if (durationRefreshAttemptRef.current >= PLAYLIST_DURATION_REFRESH_MAX_RETRIES) {
        clearPlaylistDurationRefreshMarker(playlist.id);
        stopDurationRefresh();
        return;
      }

      durationRefreshTimeoutRef.current = window.setTimeout(
        executeAttempt,
        PLAYLIST_DURATION_REFRESH_INTERVAL_MS
      );
    };

    await executeAttempt();
  }, [playlist?.id, stopDurationRefresh]);

  const markPlaylistDurationForRefresh = useCallback(
    (targetPlaylist?: IPlaylist) => {
      const playlistId = targetPlaylist?.id ?? playlist?.id;
      if (!playlistId) return;

      const baselineDurationSeconds =
        targetPlaylist?.duration_in_seconds ?? playlist?.duration_in_seconds ?? 0;

      writePlaylistDurationRefreshMarker({
        playlistId,
        baselineDurationSeconds,
      });

      runPlaylistDurationRefresh();
    },
    [playlist?.duration_in_seconds, playlist?.id, runPlaylistDurationRefresh]
  );

  const handleAddContentFinished = (updatedPlaylist?: IPlaylist) => {
    setAddContentVisible(false);
    if (updatedPlaylist) {
      maybeShowDeadlineUpdateWarning(
        updatedPlaylist.has_active_deadlines ?? playlist?.has_active_deadlines
      );
      markPlaylistDurationForRefresh(updatedPlaylist);
      refreshPlaylist();
    }
  };

  const setIsCreatingCategory = () => {
    setAddingSection(true);
    setGroupToEdit(null);
  };

  const handleAddContent = (group: IContentGroup) => {
    setCategoryToAddContent(group.name);
    setAddContentVisible(true);
  };

  const handleClonePlaylist = () => {
    setEditingPlaylist({
      open: true,
      state: {
        title: 'Clone playlist',
        confirmLabel: 'Create playlist',
        method: PLAYLIST_METHOD.DUPLICATE,
        playlistId: playlist.id,
        playlist: {
          name: `Copy of ${playlist.name}`,
          description: playlist.description || '',
          color: playlist.color,
        },
      },
    });
  };

  const handleEditPlaylist = () => {
    setEditingPlaylist({
      open: true,
      state: {
        title: 'Edit playlist',
        confirmLabel: 'Save changes',
        method: PLAYLIST_METHOD.EDIT,
        playlistId: playlist.id,
        playlist,
      },
    });
  };

  const handleEditFinish = (newData?: unknown) => {
    setEditingPlaylist(editPlaylistState);
    if (newData) {
      maybeShowDeadlineUpdateWarning(
        (newData as IPlaylist)?.has_active_deadlines ?? playlist?.has_active_deadlines
      );
      refreshPlaylist();
    }
  };

  const handleAddEditSecctionFinish = async (content) => {
    setAddingSection(false);

    const reqData: IPlaylist = {
      id: playlist.id,
      name: playlist.name,
      color: playlist.color,
      content: minimalizeContent(content),
      business_account_id,
      company: INE_UUID, // INE always
    };

    const success = await callEditPlaylist(reqData);
    if (success) {
      maybeShowDeadlineUpdateWarning(playlist?.has_active_deadlines);
      markPlaylistDurationForRefresh();
      await refreshPlaylist();
    }
  };

  const handleEditSection = (group) => {
    setGroupToEdit(group);
    setAddingSection(true);
  };

  const handleOpenAddUsers = useCallback(() => {
    if (disableAddUsers) return;
    setAddingUsers(true);
  }, [disableAddUsers]);

  const handleAddUsersFinish = () => {
    setAddingUsers(false);
  };

  const handleRefreshUsersAfterAssignmentChange = useCallback(async () => {
    await refreshPlaylist();
    await callGetUsers(isSonar);
  }, [callGetUsers, isSonar, refreshPlaylist]);

  const handleAddUsersConfirm = async () => {
    handleAddUsersFinish();
    await handleRefreshUsersAfterAssignmentChange();
  };

  const removeGroupItem = async (groupItem, group: IContentGroup) => {
    const reqData: IPlaylist = {
      id: playlist.id,
      name: playlist.name,
      color: playlist.color,
      content: generateContentFilteringGroupItem(groupItem, group),
      business_account_id,
      company: INE_UUID, // INE always
    };
    const success = await callEditPlaylist(reqData);
    if (success) {
      maybeShowDeadlineUpdateWarning(true);
      markPlaylistDurationForRefresh();
      await refreshPlaylist();
    }
  };

  const handleRemoveGroupItem = (groupItem, group: IContentGroup) => {
    setConfirmModalState({
      ...confirmModalDefaultState,
      open: true,
      description: `Are you sure you want to perform this change? There's no way to undo this action.`,
      onAccept: async () => {
        closeConfirmModal();
        await removeGroupItem(groupItem, group);
      },
    });
  };

  const handleOpenGroupItem = (groupItem) => {
    let url;
    switch (groupItem.content_type) {
      case 'course':
        url = `${INE_URL}/course/${groupItem.slug}/${groupItem.uuid}`;
        break;
      case 'link':
        url = groupItem.link_url;
        break;
      case 'lab':
        if (
          !groupItem.metadata ||
          !groupItem.metadata.courses ||
          !groupItem.metadata.courses.length
        ) {
          Notification({ type: 'info', text: 'This lab has no courses associated' });
          break;
        } else {
          // @ToDo Get the parent Course and use the Course slug to build the correct URL
          url = `${INE_URL}/course/${groupItem.lab_type}/${groupItem.metadata.courses[0]}/lab/${groupItem.uuid}`;
          break;
        }
      case 'learning-path':
        url = `${INE_URL}/path/${groupItem.uuid}`;
        break;
      case 'collection':
        url = `${INE_URL}/collections/${groupItem.uuid}`;
        break;
      case 'video':
        url = `${INE_URL}/search?search=${encodeURIComponent(groupItem.name)}&Content+Type=video`;
        break;
    }
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const removeSection = async (group: IContentGroup) => {
    const reqData: IPlaylist = {
      id: playlist.id,
      name: playlist.name,
      color: playlist.color,
      content: generateContentFilteringSection(group),
      business_account_id,
      company: INE_UUID, // INE always
    };
    const success = await callEditPlaylist(reqData);
    if (success) {
      maybeShowDeadlineUpdateWarning(true);
      markPlaylistDurationForRefresh();
      await refreshPlaylist();
    }
  };

  const handleRemoveSection = async (group: IContentGroup) => {
    setConfirmModalState({
      ...confirmModalDefaultState,
      open: true,
      description: `Are you sure you want to perform this change? There's no way to undo this action.`,
      onAccept: async () => {
        closeConfirmModal();
        await removeSection(group);
      },
    });
  };

  const closeConfirmModal = () => {
    setConfirmModalState(confirmModalDefaultState);
  };

  const handleDeletePlaylist = async () => {
    setConfirmModalState({
      ...confirmModalDefaultState,
      open: true,
      description: DELETE_PLAYLIST_CONFIRMATION_DESCRIPTION,
      onAccept: async () => {
        await callDeletePlaylist(playlist);
        closeConfirmModal();
      },
    });
  };

  const minimalizeContent = (content) => {
    const minimalizedContent = [];

    for (const group of content) {
      minimalizedContent.push({
        ...group,
        content: group.content.map(({ uuid, content_type }) => ({ uuid, content_type })),
      });
    }

    return minimalizedContent;
  };

  const handleRemoveUser = async (user) => {
    setConfirmModalState({
      ...confirmModalDefaultState,
      open: true,
      description: 'Are you sure you want to unassign this user from the playlist?',
      acceptLabel: 'Unassign user',
      cancelLabel: 'No',
      onAccept: async () => {
        const wasRemoved = await callRemoveUser(user);
        closeConfirmModal();
        if (wasRemoved) {
          await refreshPlaylist();
        }
      },
    });
  };

  const handleDragStart = async ({ type }) => {
    setDropType(type);
  };

  const generateContentSortingGroupItems = (source, destination) => {
    const clonedContent = cloneDeep(playlist.content);

    const sourceGroupIndex = clonedContent.findIndex(
      ({ name }: { name: string }) => name === source.droppableId
    );

    const sourceGroup = clonedContent[sourceGroupIndex];

    const groupItem = sourceGroup.content.splice(source.index, 1)[0];

    let destinationGroup;

    if (destination.droppableId === source.droppableId) destinationGroup = sourceGroup;
    else {
      const destinationGroupIndex = clonedContent.findIndex(
        ({ name }: { name: string }) => name === destination.droppableId
      );

      destinationGroup = clonedContent[destinationGroupIndex];
    }

    destinationGroup.content.splice(destination.index, 0, groupItem);

    return clonedContent;
  };

  const generateContentSortingGroups = (source, destination) => {
    const clonedContent = cloneDeep(playlist.content);

    const group = clonedContent.splice(source.index, 1)[0];

    clonedContent.splice(destination.index, 0, group);

    return clonedContent;
  };

  const handleDragEnd = async (payload) => {
    setCollapsed(false);
    const { source, destination } = payload;

    if (
      !destination ||
      (source.droppableId === destination.droppableId && source.index === destination.index)
    )
      return;

    const { id, name, description, color } = playlist;

    const contentSorted =
      payload.type === 'group'
        ? generateContentSortingGroups(payload.source, payload.destination)
        : generateContentSortingGroupItems(payload.source, payload.destination);

    setPlaylist((prevState) => ({ ...prevState, content: contentSorted }));

    const reqData: IPlaylist = {
      id,
      name,
      description,
      color,
      content: minimalizeContent(contentSorted),
      business_account_id,
      company: INE_UUID, // INE always
    };

    const success = await callEditPlaylist(reqData);
    if (success) {
      maybeShowDeadlineUpdateWarning(playlist?.has_active_deadlines);
      await refreshPlaylist();
    }

    setDropType('');
  };

  const handleBeforeDrag = (draggableData) => {
    if (!validUUID(draggableData.draggableId)) setCollapsed(true);
  };

  const handleCollapsed = (id: string, value: boolean) => {
    setLearningPathCollapsed((prevState) => ({
      ...prevState,
      [id]: !value,
    }));
  };

  useEffect(() => {
    refreshPlaylist();
  }, []);

  useEffect(() => {
    runPlaylistDurationRefresh();
  }, [playlist?.id, runPlaylistDurationRefresh]);

  useEffect(() => {
    setShowDeadlineUpdateWarning(false);
  }, [playlist?.id]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearDurationRefreshTimeout();
    };
  }, [clearDurationRefreshTimeout]);

  useEffect(() => {
    if (!hasStartedUsersFetch && (fetching || fetchingUsers)) {
      setHasStartedUsersFetch(true);
    }
  }, [fetching, fetchingUsers, hasStartedUsersFetch]);

  useEffect(() => {
    if (!hasLoadedUsersOnce && hasStartedUsersFetch && !fetching && !fetchingUsers) {
      setHasLoadedUsersOnce(true);
    }
  }, [fetching, fetchingUsers, hasLoadedUsersOnce, hasStartedUsersFetch]);

  const handleUnassignTeamsChange = async (
    teams: import('components/TeamSelector/types').TeamProps[]
  ) => {
    const teamIds = teams.map((team) => team.id).filter(Boolean);
    if (!teamIds.length || !playlist?.id) return;

    setFetching(true);
    try {
      const response = await updatePlaylistRule({
        playlist_id: playlist.id,
        scope: PLAYLIST_RULE_SCOPE_TEAM,
        team_ids: teamIds,
      });
      const ok = response?.status >= 200 && response?.status < 300;
      if (ok) {
        const teamLabel = teamIds.length === 1 ? 'Team' : 'Teams';

        Notification({
          text: `${teamLabel} unassigned from playlist`,
          type: 'success',
          singleLine: true,
        });

        await callGetUsers(isSonar);
      } else {
        Notification({
          text: 'There has been a problem unassigning team(s) from the playlist',
          type: 'error',
        });
      }
    } catch (error) {
      const msg = error?.response?.data?.details
        ? error.response.data.details
        : 'There has been a problem unassigning teams';
      Notification({ text: msg, type: 'error' });
    }
    setFetching(false);
  };

  const teamDeadlineRows = useMemo<TeamDeadlineRow[]>(() => {
    const assigned = playlistUsersSummary?.teams_assigned || [];

    return assigned.reduce<TeamDeadlineRow[]>((acc, assignedTeam) => {
      const fullTeam = teams?.find((t) => t.id === assignedTeam.id);
      if (!fullTeam) return acc;

      acc.push({
        teamId: assignedTeam.id,
        name: assignedTeam.name,
        iconName: fullTeam.icon?.name,
        iconColor: fullTeam.icon?.color,
        activeUsersCount: assignedTeam.active_users_count,
        deadlinePolicy: assignedTeam.deadline_policy ?? null,
        canManage: canManageTeamDeadline(Boolean(isAccountOwner), userTeams, assignedTeam.id),
      });

      return acc;
    }, []);
  }, [playlistUsersSummary, teams, userTeams, isAccountOwner]);

  const showTeamDeadlineRowsGate = Boolean(playlistDeadlinesPhase2);
  const showTeamDeadlineDrawerGate = showTeamDeadlineRowsGate && showTeamsAssigned;

  const [teamDeadlineDrawerTeamId, setTeamDeadlineDrawerTeamId] = useState<string | null>(null);
  const [teamDeadlineDrawerVisible, setTeamDeadlineDrawerVisible] = useState(false);
  const [teamDeadlinePolicy, setTeamDeadlinePolicy] = useState<DeadlinePolicy | null>(null);
  const [openingTeamDeadlineDrawerTeamId, setOpeningTeamDeadlineDrawerTeamId] = useState<
    string | null
  >(null);

  const teamDeadlineDrawerRow = useMemo(
    () => teamDeadlineRows.find((row) => row.teamId === teamDeadlineDrawerTeamId) ?? null,
    [teamDeadlineRows, teamDeadlineDrawerTeamId]
  );

  const handleOpenTeamDeadlineDrawer = useCallback(
    async (teamId: string) => {
      if (!playlist?.id) return;

      const requestId = ++openTeamDeadlineRequestIdRef.current;
      setOpeningTeamDeadlineDrawerTeamId(teamId);
      try {
        const { teamPolicies } = await getDeadlinePolicy(playlist.id, {
          scope: 'team',
          team_ids: [teamId],
        });
        if (isMountedRef.current && requestId === openTeamDeadlineRequestIdRef.current) {
          const policy = teamPolicies.find((teamPolicy) => teamPolicy.team_id === teamId) ?? null;
          setTeamDeadlinePolicy(policy);
          setTeamDeadlineDrawerTeamId(teamId);
          setTeamDeadlineDrawerVisible(true);
        }
      } catch (error) {
        if (requestId === openTeamDeadlineRequestIdRef.current) {
          Notification({ type: 'error', text: TEAM_DEFAULT_DEADLINE_ERROR_MESSAGE });
        }
      } finally {
        if (isMountedRef.current && requestId === openTeamDeadlineRequestIdRef.current) {
          setOpeningTeamDeadlineDrawerTeamId(null);
        }
      }
    },
    [playlist?.id]
  );

  const handleCloseTeamDeadlineDrawer = useCallback(() => {
    setTeamDeadlineDrawerVisible(false);
  }, []);

  const handleTeamDeadlineSuccess = useCallback(async () => {
    await callGetAssignedTeams(isSonar);
    await refreshPlaylist();
    await callGetUsers(isSonar);
  }, [callGetAssignedTeams, callGetUsers, isSonar, refreshPlaylist]);

  const handleRemoveTeamAssignment = useCallback(
    (teamId: string) => {
      if (!playlist?.id) return;

      setConfirmModalState({
        ...confirmModalDefaultState,
        open: true,
        description: REMOVE_TEAM_ASSIGNMENT_CONFIRMATION_DESCRIPTION,
        acceptLabel: 'Remove team',
        cancelLabel: 'No',
        onAccept: async () => {
          closeConfirmModal();
          try {
            await updatePlaylistRule({
              playlist_id: playlist.id,
              scope: PLAYLIST_RULE_SCOPE_TEAM,
              team_ids: [teamId],
            });
            Notification({
              text: 'Team assignment removed from playlist',
              type: 'success',
              singleLine: true,
            });
            await callGetUsers(isSonar);
          } catch (error) {
            Notification({
              text: 'There has been a problem removing the team assignment',
              type: 'error',
            });
          }
        },
      });
    },
    [playlist?.id, isSonar, callGetUsers]
  );

  useEffect(() => {
    const nextLearningPathCollapsed =
      playlist?.content?.reduce<Record<string, boolean>>((acc, contentGroup) => {
        contentGroup.content.forEach((contentItem) => {
          if (EXPANDABLE_CONTENT_TYPES.includes(contentItem.content_type)) {
            acc[contentItem.uuid] = false;
          }
        });
        return acc;
      }, {}) ?? {};

    setLearningPathCollapsed((prev) =>
      reconcileCollapsedState(prev, Object.keys(nextLearningPathCollapsed))
    );
  }, [playlist.content]);

  useEffect(() => {
    setHasStartedUsersFetch(true);
    callGetUsers(isSonar);
  }, [isSonar, playlist.id]);

  const getContentCount = (length: number) => {
    return length === 1 ? `${length} Item` : `${length} Items`;
  };

  return (
    <PlaylistsProvider>
      <MainContent lock={fetching}>
        {!isSonar && (
          <PlaylistDeadlineWarningBanner
            hasActiveDeadlines={Boolean(playlist?.has_active_deadlines)}
            visible={showDeadlineUpdateWarning}
          />
        )}
        {isAccountPlaylistFeatureEnabled && accountPlaylistState.isAccountPlaylist && (
          <AccountPlaylistBanner>
            <AccountPlaylistBannerEmphasis>
              This playlist is assigned to all licensed users on the account.
            </AccountPlaylistBannerEmphasis>
            <AccountPlaylistBannerDescription>
              Any updates you make will automatically apply to their assigned versions.
            </AccountPlaylistBannerDescription>
          </AccountPlaylistBanner>
        )}

        <Header isSonar={isSonar} sonarBackground={sonarBackground}>
          {isSonarRecommended && <img src={SonarPlaylistLogo} alt="Sonar Playlist" />}
          <HeaderTitle>
            {isAccountPlaylistFeatureEnabled && accountPlaylistState.isAccountPlaylist && (
              <HeaderAccountLabel>
                <AccountPlaylistBadge />
                {account?.name} Playlist
              </HeaderAccountLabel>
            )}
            <HeaderTitleText>{playlist.name}</HeaderTitleText>
          </HeaderTitle>
          {playlist.duration_in_seconds > 0 && (
            <DurationPill bgColor="#1A2535">
              {isPlaylistDurationRefreshing ? (
                <DurationPillSpinnerContainer data-testid="playlist-detail-duration-refresh-spinner">
                  <Spinner active position="absolute" top="50%" left="50%" size="14" />
                </DurationPillSpinnerContainer>
              ) : (
                durationFromSecs(playlist.duration_in_seconds)
              )}
            </DurationPill>
          )}
        </Header>
        <SubHeader>
          {!isSonar ? (
            <Description>{playlist.description}</Description>
          ) : (
            <SonarHeaderInfo>
              {fetchingUsers ? (
                <Skeleton paragraph={{ rows: 0 }} style={{ margin: '-12px 0px -20px' }} />
              ) : (
                <span>Description: {playlist?.description}</span>
              )}
            </SonarHeaderInfo>
          )}
          <ButtonsWrapper gap={14}>
            <Tooltip placement="top" title="Duplicate playlist">
              <ButtonIcon
                $secondary
                className="playlist__button-icon"
                size="small"
                icon="Duplicate"
                onClick={handleClonePlaylist}
              />
            </Tooltip>
            {!isSonar && (
              <>
                <Tooltip placement="top" title="Edit playlist">
                  <ButtonIcon
                    $secondary
                    className="playlist__button-icon"
                    size="small"
                    icon="Edit"
                    onClick={handleEditPlaylist}
                  />
                </Tooltip>
                <Tooltip placement="top" title="Delete playlist">
                  <ButtonIcon
                    $secondary
                    className="playlist__button-icon"
                    size="small"
                    icon="Trash"
                    onClick={handleDeletePlaylist}
                  />
                </Tooltip>
              </>
            )}
            {isSonar && !playlist.assigned_user_ids?.length && (
              <AssignPlaylistButton
                playlist={playlist}
                setFetching={setFetching}
                onRefreshAfterAssignment={handleRefreshUsersAfterAssignmentChange}
              />
            )}
          </ButtonsWrapper>
        </SubHeader>

        <TabsWrapper>
          <Tabs
            items={tabItems}
            selected={selectedTab}
            $flex={!isScreenSm ? 'fill' : null}
            onTabChange={(tab) => setSelectedTab(tab)}
          />
          {!isSonar && (
            <FixedBottomButton onClick={setIsCreatingCategory} size={'small'} icon={'Plus'}>
              Add section
            </FixedBottomButton>
          )}
        </TabsWrapper>
        {isScreenLg || selectedTab === 'content' ? (
          <ContentWrapper>
            <ContentHeaderWrapper>
              <ContentTitleWrapper isSonar={isSonar}>
                <ContentTitle>Playlist Content</ContentTitle>
                {!isSonar ? (
                  <Button onClick={setIsCreatingCategory} icon={'Plus'}>
                    Add section
                  </Button>
                ) : (
                  isSonarRecommended && (
                    <SonarHeaderInfo>
                      {fetchingUsers ? (
                        <Skeleton paragraph={{ rows: 0 }} style={{ margin: '-12px 0' }} />
                      ) : (
                        <>
                          As <b style={{ color: 'var(--black-dark)' }}>{username}</b> completes
                          Skill Sonar assessments, the content in this playlist will update
                          automatically.
                        </>
                      )}
                    </SonarHeaderInfo>
                  )
                )}
              </ContentTitleWrapper>
              {isSonar && (
                <SonarHeaderActivity>
                  Last modified <b> {modifiedAgo}</b>.
                  <span
                    onClick={() => {
                      setShowChangelog(true);
                    }}
                  >
                    See change log
                  </span>
                </SonarHeaderActivity>
              )}
            </ContentHeaderWrapper>
            <DragDropContext
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onBeforeCapture={handleBeforeDrag}
            >
              <Droppable
                droppableId="main-content"
                type="group"
                isDropDisabled={dropType !== 'group'}
              >
                {(droppableProvided) => (
                  <div
                    className="group-list"
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                  >
                    {playlist.content && playlist.content.length
                      ? playlist.content.map((group, index) => (
                          <Draggable
                            key={group.name}
                            draggableId={group.name}
                            index={index}
                            isDragDisabled={isSonar}
                          >
                            {(draggableProvided) => (
                              <SectionContentWrapper
                                ref={draggableProvided.innerRef}
                                {...draggableProvided.draggableProps}
                              >
                                <SectionHeader>
                                  <div>
                                    <SectionTitle>
                                      {group.name}
                                      {!isSonar && (
                                        <ButtonWrapper onClick={() => handleEditSection(group)}>
                                          <Icon
                                            size="small"
                                            icon="Edit"
                                            color={colors.buttons.darkPurple}
                                          />
                                        </ButtonWrapper>
                                      )}
                                    </SectionTitle>
                                    <SmallInfo>{getContentCount(group.content.length)}</SmallInfo>
                                  </div>

                                  <ButtonsWrapper gap={20}>
                                    {!isSonar && isScreenSm && (
                                      <Button
                                        $secondary
                                        onClick={() => handleAddContent(group)}
                                        icon={'Plus'}
                                      >
                                        Add content
                                      </Button>
                                    )}
                                    {!isSonar && (
                                      <ButtonIcon
                                        $secondary
                                        size="small"
                                        icon="Trash"
                                        aria-label={`Remove section ${group.name}`}
                                        onClick={() => handleRemoveSection(group)}
                                      />
                                    )}

                                    <div {...draggableProvided.dragHandleProps}>
                                      {!isSonar && (
                                        <Tooltip placement="top" title={DRAG_TO_REARRANGE_LABEL}>
                                          <DragHandleWrapper>
                                            <Icon icon="DragHandle" size="default" />
                                          </DragHandleWrapper>
                                        </Tooltip>
                                      )}
                                    </div>
                                  </ButtonsWrapper>
                                </SectionHeader>
                                {!collapsed && (
                                  <Droppable
                                    droppableId={group.name}
                                    type="group-item"
                                    isDropDisabled={dropType !== 'group-item'}
                                  >
                                    {(droppableGroupItemProvided, droppableGroupItemSnapshot) => (
                                      <GroupItemsContainer
                                        isDraggingOver={droppableGroupItemSnapshot.isDraggingOver}
                                        isEmpty={!group.content.length}
                                        ref={droppableGroupItemProvided.innerRef}
                                        {...droppableGroupItemProvided.droppableProps}
                                      >
                                        {group.content?.length
                                          ? group.content.map((groupItem, index) => (
                                              <Draggable
                                                key={groupItem.uuid}
                                                draggableId={groupItem.uuid}
                                                index={index}
                                                isDragDisabled={isSonar}
                                              >
                                                {(draggableGroupItemProvided) => (
                                                  <>
                                                    <GroupItemRow
                                                      ref={draggableGroupItemProvided.innerRef}
                                                      {...draggableGroupItemProvided.draggableProps}
                                                    >
                                                      <GroupItemImg
                                                        image={
                                                          groupItem.images?.small_image_url ||
                                                          groupItem.images?.medium_image_url ||
                                                          groupItem.images?.main_image_url ||
                                                          groupItem.logo_image_url
                                                        }
                                                        disabled={isUnopenable(groupItem)}
                                                        onClick={() =>
                                                          !isUnopenable(groupItem) &&
                                                          handleOpenGroupItem(groupItem)
                                                        }
                                                      />
                                                      <GroupItemMainWrapper>
                                                        {groupItem.content_type === 'course' && (
                                                          <CourseItemData
                                                            data={groupItem}
                                                            onOpen={openHandlerFor(groupItem)}
                                                          />
                                                        )}
                                                        {groupItem.content_type === 'link' && (
                                                          <LinkItemData
                                                            data={groupItem}
                                                            onSave={handlePatchLink}
                                                          />
                                                        )}
                                                        {groupItem.content_type === 'video' && (
                                                          <VideoItemData
                                                            data={groupItem}
                                                            onOpen={openHandlerFor(groupItem)}
                                                          />
                                                        )}
                                                        {groupItem.content_type === 'lab' && (
                                                          <LabItemData
                                                            data={groupItem}
                                                            onOpen={openHandlerFor(groupItem)}
                                                          />
                                                        )}
                                                        {groupItem.content_type ===
                                                          'learning-path' && (
                                                          <LearningPathItemData
                                                            data={groupItem}
                                                            onOpen={openHandlerFor(groupItem)}
                                                            toggleDetails={() =>
                                                              handleCollapsed(
                                                                groupItem.uuid,
                                                                learningPathCollapsed?.[
                                                                  groupItem.uuid
                                                                ]
                                                              )
                                                            }
                                                          />
                                                        )}
                                                        {groupItem.content_type ===
                                                          'collection' && (
                                                          <CollectionItemData
                                                            data={groupItem}
                                                            onOpen={openHandlerFor(groupItem)}
                                                            locked={isContentLocked(groupItem)}
                                                            toggleDetails={() =>
                                                              handleCollapsed(
                                                                groupItem.uuid,
                                                                learningPathCollapsed?.[
                                                                  groupItem.uuid
                                                                ]
                                                              )
                                                            }
                                                          />
                                                        )}
                                                        <ButtonsWrapper>
                                                          {EXPANDABLE_CONTENT_TYPES.includes(
                                                            groupItem.content_type
                                                          ) &&
                                                            !isSonar &&
                                                            !isMobile && (
                                                              <ButtonIcon
                                                                $secondary
                                                                size="small"
                                                                icon={
                                                                  learningPathCollapsed?.[
                                                                    groupItem.uuid
                                                                  ]
                                                                    ? 'ChevronUp'
                                                                    : 'ChevronDown'
                                                                }
                                                                onClick={() =>
                                                                  handleCollapsed(
                                                                    groupItem.uuid,
                                                                    learningPathCollapsed?.[
                                                                      groupItem.uuid
                                                                    ]
                                                                  )
                                                                }
                                                              />
                                                            )}
                                                          {!isSonar && (
                                                            <>
                                                              <ButtonIcon
                                                                $secondary
                                                                size="small"
                                                                icon="Trash"
                                                                aria-label={`Remove item ${groupItem.name}`}
                                                                onClick={() =>
                                                                  handleRemoveGroupItem(
                                                                    groupItem,
                                                                    group
                                                                  )
                                                                }
                                                              />

                                                              <div
                                                                {...draggableGroupItemProvided.dragHandleProps}
                                                              >
                                                                <Tooltip
                                                                  placement="top"
                                                                  title={DRAG_TO_REARRANGE_LABEL}
                                                                >
                                                                  <DragHandleWrapper>
                                                                    <Icon
                                                                      icon="DragHandle"
                                                                      size="default"
                                                                    />
                                                                  </DragHandleWrapper>
                                                                </Tooltip>
                                                              </div>
                                                            </>
                                                          )}
                                                        </ButtonsWrapper>
                                                      </GroupItemMainWrapper>
                                                    </GroupItemRow>
                                                    {learningPathCollapsed?.[groupItem.uuid] &&
                                                      (groupItem.content_type === 'collection' ? (
                                                        <CollectionDetail data={groupItem} />
                                                      ) : (
                                                        <LearningPathDetail data={groupItem} />
                                                      ))}
                                                  </>
                                                )}
                                              </Draggable>
                                            ))
                                          : !isSonar && (
                                              <GroupItemsEmpty>
                                                <span onClick={() => handleAddContent(group)}>
                                                  Add
                                                </span>{' '}
                                                or drag and drop content here
                                              </GroupItemsEmpty>
                                            )}

                                        {!isScreenSm && !isSonar && (
                                          <Button
                                            $full
                                            $secondary
                                            size="small"
                                            onClick={() => handleAddContent(group)}
                                            icon={'Plus'}
                                          >
                                            Add content
                                          </Button>
                                        )}
                                      </GroupItemsContainer>
                                    )}
                                  </Droppable>
                                )}
                              </SectionContentWrapper>
                            )}
                          </Draggable>
                        ))
                      : null}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </ContentWrapper>
        ) : null}
        {isScreenLg || selectedTab === 'users' ? (
          <ContentWrapper>
            {!isSonar && isScreenLg && (
              <ContentTitleWrapper isSonar={isSonar}>
                <ContentTitleSection>
                  <ContentTitle>Users assigned</ContentTitle>
                  <PlaylistUsersSummary>{assignedUsersSummary}</PlaylistUsersSummary>
                </ContentTitleSection>
                {shouldShowPlaylistUsersActions && (
                  <ButtonsWrapper gap={14} $disabled={disablePlaylistUsersActions}>
                    {showAccountPlaylistToggleTM && (
                      <AccountPlaylistToggle
                        playlistId={playlist.id}
                        initialIsAccountPlaylist={isAssignedToAccount}
                        isAccountOwner={false}
                        isTeamManager={isTeamManager}
                        onRefresh={handleAccountToggleRefresh}
                        onStateChange={handleAccountPlaylistChange}
                      />
                    )}
                    {showAccountPlaylistToggleAO && (
                      <AccountAssignmentControl>
                        <AccountAssignmentLabel>Account assignment</AccountAssignmentLabel>
                        <RadioPopoverDropdown
                          value={accountAssignValue}
                          options={accountAssignOptions}
                          onChange={handleAccountAssignChange}
                          disabled={
                            accountPlaylistState.isLoading || accountPlaylistState.isCoolingDown
                          }
                          title={
                            <span style={{ fontSize: 16, fontWeight: 700, color: '#1A2535' }}>
                              Manage account assignment
                            </span>
                          }
                          footer={(selectedValue) =>
                            selectedValue === 'off' && isAssignedToAccount ? (
                              <div style={{ fontSize: 12, color: '#57575C', marginBottom: 20 }}>
                                <strong style={{ color: 'var(--black-dark)', fontWeight: 700 }}>
                                  {pluralize(totalAssignedUsers, 'user')}
                                </strong>{' '}
                                assigned through this setting will be unassigned.
                              </div>
                            ) : null
                          }
                          variant="card"
                          actions={{
                            confirmLabel: 'Save',
                            showCancel: false,
                            fullWidth: true,
                            disableWhenUnchanged: true,
                          }}
                          popoverProps={{
                            placement: 'bottomRight',
                            overlayInnerStyle: {
                              borderRadius: 16,
                              boxShadow: '0px 20px 50px rgba(0, 0, 0, 0.12)',
                              padding: '8px 4px',
                              width: 367,
                            },
                            overlayStyle: { padding: 0 },
                          }}
                          renderTrigger={({ open, disabled }) => (
                            <AccountAssignmentTrigger
                              type="button"
                              $open={open}
                              disabled={disabled}
                            >
                              <span>{accountAssignTriggerLabel}</span>
                              <span className="account-assignment__chevron">
                                <Icon icon="ChevronDown" size="smaller" />
                              </span>
                            </AccountAssignmentTrigger>
                          )}
                        />
                      </AccountAssignmentControl>
                    )}
                    {canShowDefaultDeadlineEntryPoint && (
                      <DefaultDeadlineButtonWrapper>
                        <Button
                          $secondary
                          disabled={defaultDeadlineLoading}
                          aria-busy={defaultDeadlineLoading}
                          onClick={handleOpenDefaultDeadlineDrawer}
                        >
                          <DefaultDeadlineIconSlot aria-hidden="true">
                            {defaultDeadlineLoading ? (
                              <LoadingOutlined />
                            ) : (
                              <Icon icon="CalendarTransparent" />
                            )}
                          </DefaultDeadlineIconSlot>
                          <span>{DEFAULT_DEADLINE_COPY.entryPointLabel}</span>
                        </Button>
                      </DefaultDeadlineButtonWrapper>
                    )}
                    {showTeamsAssigned && (
                      <PlaylistUnassignTeamSelector
                        teamsSelected={assignedTeams}
                        disabled={disableManualAssignments}
                        disabledTooltip={teamsDropdownTooltip}
                        onUnassignSubmit={handleUnassignTeamsChange}
                        wrapperStyles={{ display: 'flex', flex: '1 1 auto', minWidth: '0' }}
                        containerStyles={{ display: 'flex', flex: '1 1 auto', minWidth: '0' }}
                        teamDeadlineRows={showTeamDeadlineRowsGate ? teamDeadlineRows : undefined}
                        playlistDurationSeconds={playlist.duration_in_seconds}
                        loadingTeamId={openingTeamDeadlineDrawerTeamId}
                        onManageDeadline={handleOpenTeamDeadlineDrawer}
                        onRemoveAssignment={handleRemoveTeamAssignment}
                      />
                    )}
                    {!isAssignedToAccount && (
                      <Tooltip
                        placement="top"
                        overlayInnerStyle={{ whiteSpace: 'pre-line', textAlign: 'center' }}
                        title={addUsersTooltip}
                      >
                        <span style={{ display: 'inline-flex' }}>
                          <Button
                            $primary
                            disabled={disableAddUsers}
                            onClick={handleOpenAddUsers}
                            icon={'Plus'}
                          >
                            {addUsersLabel}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </ButtonsWrapper>
                )}
              </ContentTitleWrapper>
            )}
            {playlist?.id ? (
              <UsersAssigned
                lock={fetching}
                playlistUsers={playlistUsers}
                playlist={playlist}
                fetchingUsers={fetchingUsers}
                isSonar={isSonar}
                isAccountPlaylist={isAssignedToAccount}
                callRemoveUser={(user) => handleRemoveUser(user)}
                setFetching={setFetching}
                onRefreshAfterAssignment={handleRefreshUsersAfterAssignmentChange}
                disableManualAssignments={disableManualAssignments}
                pagination={{
                  count: playlistUsersSummary?.total_users ?? playlistUsers.length,
                  page: usersPagination.page,
                  page_size: usersPagination.page_size,
                }}
                onUsersPaginationChange={onUsersPaginationChange}
                deadlineBulkActionsEnabled={deadlineBulkActionsEnabled}
                selectedDeadlineFilters={selectedDeadlineFilters}
                sortState={sortState}
                hasAssignedUsers={totalAssignedUsersOverall > 0}
                selectedUserIds={selectedUserIds}
                allUsersSelected={allUsersSelected}
                hasPartiallySelectedUsers={hasPartiallySelectedUsers}
                onToggleSelectAllUsers={handleToggleSelectAllUsers}
                onToggleUserSelection={handleToggleUserSelection}
                onClearSelection={handleClearSelection}
                onEditDeadlines={() => setBulkDeadlineDrawerVisible(true)}
                onEditDeadline={handleOpenUserDeadline}
                onDeadlineFiltersChange={onDeadlineFiltersChange}
                onSortToggle={onSortToggle}
                onSortSelect={onSortSelect}
              />
            ) : null}
          </ContentWrapper>
        ) : null}
        {editingPlaylist.open && (
          <CreatePlaylist
            {...editingPlaylist.state}
            isVisible={editingPlaylist.open}
            onClose={(data) => handleEditFinish(data)}
          />
        )}
        {!!addContentVisible && (
          <AddContent
            playlist={playlist}
            category={categoryToAddContent}
            isVisible={addContentVisible}
            onClose={(updatedPlaylist) => handleAddContentFinished(updatedPlaylist)}
          />
        )}
        {!!addingUsers && (
          <AssignUsers
            playlistUsers={playlistUsers}
            isVisible={addingUsers}
            playlist={playlist}
            onCancel={handleAddUsersFinish}
            onConfirm={handleAddUsersConfirm}
          />
        )}
        {!!addingSection && (
          <AddEditCategory
            isVisible={addingSection}
            playlist={playlist}
            group={groupToEdit}
            onClose={() => setAddingSection(false)}
            onConfirm={handleAddEditSecctionFinish}
          />
        )}
        {playlist?.id && playlistDeadlinesMvp && !isSonar && selectedDeadlineUser && (
          <ManageDeadlineDrawer
            visible={Boolean(selectedDeadlineUser)}
            onClose={handleCloseUserDeadline}
            onSuccess={handleDeadlineSuccess}
            playlistId={playlist.id}
            businessAccountId={playlist.business_account_id ?? account.id}
            playlistDurationSeconds={playlist.duration_in_seconds}
            userId={selectedDeadlineUser.userId}
            currentDeadline={selectedDeadlineUser.currentDeadline}
            userProgress={selectedDeadlineUserProgress}
            isPlaylistDurationRefreshing={isPlaylistDurationRefreshing}
          />
        )}
        {playlist?.id && deadlineBulkActionsEnabled && selectedUserIds.length > 0 && (
          <ManageDeadlineDrawer
            visible={bulkDeadlineDrawerVisible}
            onClose={() => setBulkDeadlineDrawerVisible(false)}
            onSuccess={handleBulkDeadlineSuccess}
            playlistId={playlist.id}
            businessAccountId={playlist.business_account_id ?? account.id}
            playlistDurationSeconds={playlist.duration_in_seconds}
            userIds={selectedUserIds}
            userProgress={singleSelectedBulkUserProgress}
            hasCompletedUsersInBulkSelection={hasCompletedUsersInBulkSelection}
            isPlaylistDurationRefreshing={isPlaylistDurationRefreshing}
          />
        )}
        {playlist?.id && canShowDefaultDeadlineEntryPoint && (
          <DefaultDeadlineDrawer
            visible={defaultDeadlineDrawerVisible}
            onClose={handleCloseDefaultDeadlineDrawer}
            onSuccess={handleDefaultDeadlineSuccess}
            playlistId={playlist.id}
            businessAccountId={playlist.business_account_id ?? account.id}
            playlistDurationSeconds={playlist.duration_in_seconds}
            currentPolicy={defaultDeadlinePolicy}
            hasAssignedUsers={totalAssignedUsersOverall > 0}
          />
        )}
        {playlist?.id && showTeamDeadlineDrawerGate && teamDeadlineDrawerTeamId && (
          <DefaultDeadlineDrawer
            visible={teamDeadlineDrawerVisible}
            onClose={handleCloseTeamDeadlineDrawer}
            onSuccess={handleTeamDeadlineSuccess}
            playlistId={playlist.id}
            businessAccountId={playlist.business_account_id ?? account.id}
            playlistDurationSeconds={playlist.duration_in_seconds}
            currentPolicy={teamDeadlinePolicy}
            hasAssignedUsers={Boolean(teamDeadlineDrawerRow?.activeUsersCount)}
            scope={PLAYLIST_RULE_SCOPE_TEAM}
            teamId={teamDeadlineDrawerTeamId}
          />
        )}
        <ConfirmModal {...confirmModalState} title="Are you sure?" onCancel={closeConfirmModal} />
        {fetching && (
          <Spinner active={fetching} position="fixed" top="50%" left="50%" zIndex="1000" />
        )}
        {showChangelog && (
          <PlaylistChangelogModal
            isVisible={showChangelog}
            playlist={playlist}
            userName={username}
            onClose={() => {
              setShowChangelog(false);
            }}
          />
        )}
      </MainContent>
    </PlaylistsProvider>
  );
};

export default PlaylistDetail;
